// The Design workspace's Structure panel: forms → groups → columns as the
// record has them, nested child forms under their parent, "How lines are
// filled" on every line form. Everything the old node tree offered that was
// NOT structure (actions, validations, joins, record hooks and settings) now
// lives on the canvas's form ▾ / Whole record ▾ menus, the setup lists under
// the form card, and the Logic / Settings workspaces.

import { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import toast from 'react-hot-toast'
import { ChevronRight, ChevronDown, Plus, MoreHorizontal, Search, PanelLeftClose, X } from 'lucide-react'
import type { TransactionModel, TreeNode } from './types'
import { clearColumnDrag, setColumnDrag } from './columnDrag'
import {
  allSqlModels, childFormsOf, descendantFormNos, headerFormNo,
} from '@twasta/page-runtime/txn/utils'
import { makeChildForm, makeUnlinkedForm } from './formFactory'
import { groupColumns, groupLabel, columnLogicCount, formTitle, lineEntryCount } from './setupCatalog'
import { useConfirm } from '@/components/common/ConfirmDialog'

interface TreeViewProps {
  model: TransactionModel
  selectedNode: TreeNode | null
  onSelect: (node: TreeNode) => void
  // Optional: when provided, the panel offers Add form / Delete form. Omit it
  // for a read-only tree.
  onModelChange?: (model: TransactionModel) => void
  /** One step back in history — the Undo on the "form deleted" toast. */
  onUndo?: () => void
  /** Hide the panel (the `[` key does the same). */
  onHide?: () => void
  /** Phone drawer: show a close button instead of the hide-panel icon. */
  drawer?: boolean
}

const truthy = (v: unknown) => v === true || v === 'true' || v === '1' || v === 1

const iconBtn: React.CSSProperties = {
  width: 28, height: 28, border: 'none', background: 'transparent', borderRadius: 6,
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
  color: 'var(--color-text)', flexShrink: 0,
}

const menuStyle: React.CSSProperties = {
  position: 'absolute', zIndex: 40, minWidth: 220, background: 'var(--color-bg-secondary)',
  border: '1px solid var(--color-border)', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
  padding: 6,
}

const menuItem: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', font: 'inherit',
  fontSize: '0.9375rem', padding: '7px 10px', border: 'none', background: 'transparent',
  borderRadius: 4, cursor: 'pointer', color: 'var(--color-text)',
}

export default function TreeView({ model, selectedNode, onSelect, onModelChange, onUndo, onHide, drawer }: TreeViewProps) {
  const { confirm, confirmDialog } = useConfirm()
  // Collapsed state keyed by `f:<formNo>` / `g:<formNo>:<group>`; everything
  // starts open except groups of forms other than the selected one.
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({})
  const [filter, setFilter] = useState('')
  const [menu, setMenu] = useState<null | { kind: 'add' } | { kind: 'form'; fi: number }>(null)
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!menu) return
    const close = (e: MouseEvent) => {
      if (rootRef.current && e.target instanceof Node && rootRef.current.querySelector('[data-tv-menu]')?.contains(e.target)) return
      setMenu(null)
    }
    window.addEventListener('mousedown', close)
    return () => window.removeEventListener('mousedown', close)
  }, [menu])

  const toggle = useCallback((key: string) => {
    setCollapsed(prev => ({ ...prev, [key]: !prev[key] }))
  }, [])

  const entries = useMemo(() => allSqlModels(model), [model])
  const fiByFormNo = useMemo(() => new Map(entries.map((e, i) => [e.formNo, i])), [entries])
  const headerNo = headerFormNo(model)

  const addChildForm = (parentFormNo: string) => {
    if (!onModelChange) return
    const m: TransactionModel = JSON.parse(JSON.stringify(model))
    m.sql_models = [...m.sql_models, makeChildForm(model, parentFormNo)]
    onModelChange(m)
    onSelect({ type: 'form', formIndex: m.sql_models.length - 1, label: 'New form' })
    setMenu(null)
  }

  const addUnlinkedForm = () => {
    if (!onModelChange) return
    const m: TransactionModel = JSON.parse(JSON.stringify(model))
    m.sql_models = [...m.sql_models, makeUnlinkedForm(model)]
    onModelChange(m)
    onSelect({ type: 'form', formIndex: m.sql_models.length - 1, label: 'New form' })
    setMenu(null)
  }

  const removeForm = async (fi: number) => {
    setMenu(null)
    if (!onModelChange) return
    const title = formTitle(model.sql_models[fi]?.sql_model, fi)
    const ok = await confirm({
      title: 'Delete form?',
      message: <>Delete <b>{title}</b> and all its columns, buttons and rules?</>,
      confirmLabel: 'Delete form',
    })
    if (!ok) return
    const m: TransactionModel = JSON.parse(JSON.stringify(model))
    m.sql_models = m.sql_models.filter((_, i) => i !== fi)
    onModelChange(m)
    onSelect({ type: 'form', formIndex: 0, label: formTitle(m.sql_models[0]?.sql_model, 0) })
    if (onUndo) {
      toast((t) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
          Form {title} deleted
          <button className="btn btn-ghost btn-sm" onClick={() => { onUndo(); toast.dismiss(t.id) }}>Undo</button>
        </span>
      ), { duration: 6000 })
    }
  }

  const selColumn = (fi: number, ci: number) =>
    selectedNode?.type === 'column' && selectedNode.formIndex === fi && selectedNode.columnIndex === ci
  const selForm = (fi: number) =>
    selectedNode?.formIndex === fi && selectedNode.type !== 'column' && selectedNode.type !== 'form-line-entry'
  const selLines = (fi: number) => selectedNode?.type === 'form-line-entry' && selectedNode.formIndex === fi

  const rowStyle = (depth: number, sel: boolean): React.CSSProperties => ({
    display: 'flex', alignItems: 'center', gap: 6, minHeight: drawer ? 44 : 30, borderRadius: 6,
    padding: `0 6px 0 ${6 + depth * 16}px`, cursor: 'pointer', userSelect: 'none',
    background: sel ? 'var(--color-accent-weak)' : 'transparent',
    outline: sel ? '1px solid var(--color-accent)' : 'none', outlineOffset: -1,
  })
  const caret = (key: string, open: boolean) => (
    <span
      role="button"
      aria-label={open ? 'Collapse' : 'Expand'}
      onClick={(e) => { e.stopPropagation(); toggle(key) }}
      style={{ width: 16, display: 'inline-flex', justifyContent: 'center', flexShrink: 0, color: 'var(--color-text-secondary)' }}
    >
      {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
    </span>
  )

  const q = filter.trim().toLowerCase()

  const renderColumns = (fi: number, cols: Array<{ col: TransactionModel['sql_models'][number]['sql_model']['columns'][number]['column']; index: number }>, depth: number) =>
    cols.map(({ col: c, index: ci }) => {
      const sel = selColumn(fi, ci)
      const logic = columnLogicCount(c)
      return (
        <div
          key={`c-${fi}-${ci}`}
          style={rowStyle(depth, sel)}
          onClick={() => onSelect({ type: 'column', formIndex: fi, columnIndex: ci, label: c.name })}
          draggable={!!c.db_name}
          onDragStart={(e) => {
            if (!c.db_name) return
            setColumnDrag(e, { formIndex: fi, name: c.db_name, label: c.heading || c.name || c.db_name })
          }}
          onDragEnd={clearColumnDrag}
          title={c.db_name ? `${c.heading || c.name} — drag into a Feed view well` : undefined}
        >
          <span style={{ width: 16, flexShrink: 0 }} />
          <span style={{
            fontFamily: 'var(--font-mono)', fontSize: '0.875rem', minWidth: 0, overflow: 'hidden',
            textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: sel ? 'var(--color-accent)' : 'var(--color-text)',
            opacity: truthy(c.hidden) ? 0.6 : 1,
          }}>{c.name}</span>
          {truthy(c.key) && (
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.75rem', color: 'var(--color-warning, #9a6700)', border: '1px solid var(--color-border)', borderRadius: 3, padding: '0 4px' }}>PK</span>
          )}
          {truthy(c.mandatory) && <span aria-label="mandatory" style={{ color: 'var(--color-error)', fontWeight: 600 }}>*</span>}
          {logic > 0 && (
            <span title={`${logic} piece${logic === 1 ? '' : 's'} of logic (lookup, change, validations)`} style={{
              fontSize: '0.75rem', color: 'var(--color-accent)', background: 'var(--color-accent-weak)', borderRadius: 3, padding: '0 4px',
            }}>ƒ {logic}</span>
          )}
        </div>
      )
    })

  const renderForm = (formNo: string, depth: number, visited: Set<string>): React.ReactNode => {
    if (visited.has(formNo)) return null
    visited.add(formNo)
    const fi = fiByFormNo.get(formNo)
    if (fi === undefined) return null
    const sm = entries[fi].sm
    const isHeader = formNo === headerNo
    const fKey = `f:${formNo}`
    const open = !collapsed[fKey]
    const kids = childFormsOf(model, formNo)
    const hasDescendants = descendantFormNos(model, formNo).size > 0
    const groups = groupColumns(sm)
    const canDelete = !isHeader && !hasDescendants
    return (
      <div key={`f-${formNo}`}>
        <div style={rowStyle(depth, selForm(fi))} onClick={() => onSelect({ type: 'form', formIndex: fi, label: formTitle(sm, fi) })}>
          {caret(fKey, open)}
          <span style={{
            minWidth: 20, height: 18, borderRadius: 4, padding: '0 4px', background: 'var(--color-accent-weak)',
            color: 'var(--color-accent)', fontSize: '0.75rem', fontWeight: 600, display: 'inline-flex',
            alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>{formNo}</span>
          <span style={{ fontWeight: 600, fontSize: '0.9375rem', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {formTitle(sm, fi)}
          </span>
          {!isHeader && <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>{(sm.columns || []).length} cols</span>}
          {onModelChange && (
            <span style={{ position: 'relative' }}>
              <button type="button" aria-label={`${formTitle(sm, fi)} options`} title="Form options"
                style={{ ...iconBtn, width: 24, height: 24 }}
                onClick={(e) => { e.stopPropagation(); setMenu(menu?.kind === 'form' && menu.fi === fi ? null : { kind: 'form', fi }) }}>
                <MoreHorizontal size={14} />
              </button>
              {menu?.kind === 'form' && menu.fi === fi && (
                <div data-tv-menu role="menu" style={{ ...menuStyle, right: 0, top: '100%' }} onClick={e => e.stopPropagation()}>
                  <button role="menuitem" style={menuItem} onClick={() => addChildForm(formNo)}>
                    <Plus size={13} /> Add a child form
                  </button>
                  <button role="menuitem" style={{ ...menuItem, color: canDelete ? 'var(--color-error)' : 'var(--color-text-faint)', cursor: canDelete ? 'pointer' : 'default' }}
                    disabled={!canDelete}
                    title={isHeader ? 'The header form cannot be deleted' : hasDescendants ? 'Delete its child forms first' : undefined}
                    onClick={() => { if (canDelete) void removeForm(fi) }}>
                    Delete form…
                  </button>
                </div>
              )}
            </span>
          )}
        </div>
        {open && (
          <>
            {!isHeader && (
              <div style={rowStyle(depth + 1, selLines(fi))} onClick={() => onSelect({ type: 'form-line-entry', formIndex: fi, label: 'How lines are filled' })}>
                <span style={{ width: 16, flexShrink: 0 }} />
                <span style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--color-accent)', flex: 1 }}>How lines are filled</span>
                <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>{lineEntryCount(sm)}</span>
              </div>
            )}
            {groups.map(([gname, cols]) => {
              const gKey = `g:${formNo}:${gname}`
              const gOpen = !collapsed[gKey]
              return (
                <div key={gKey}>
                  <div style={rowStyle(depth + 1, false)} onClick={() => toggle(gKey)}>
                    {caret(gKey, gOpen)}
                    <span style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--color-text-secondary)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {groupLabel(sm, gname)}
                    </span>
                    <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>{cols.length}</span>
                  </div>
                  {gOpen && renderColumns(fi, cols, depth + 2)}
                </div>
              )
            })}
            {(sm.columns || []).length === 0 && (
              <div style={{ ...rowStyle(depth + 1, false), cursor: 'default', fontSize: '0.8125rem', color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>
                <span style={{ width: 16 }} />No columns yet
              </div>
            )}
            {kids.map(k => renderForm(k.formNo, depth + 1, visited))}
          </>
        )}
      </div>
    )
  }

  const renderTree = (): React.ReactNode => {
    if (entries.length === 0) return null
    const visited = new Set<string>()
    const nodes: React.ReactNode[] = [renderForm(headerNo, 0, visited)]
    // A form whose parent chain is broken still shows, flat, at the end.
    for (const e of entries) if (!visited.has(e.formNo)) nodes.push(renderForm(e.formNo, 0, visited))
    return nodes
  }

  // Filtered view: matching columns with where they live.
  const renderFiltered = () => {
    const hits: React.ReactNode[] = []
    entries.forEach((e, fi) => {
      for (const [gname, cols] of groupColumns(e.sm)) {
        const match = cols.filter(({ col }) =>
          [col.name, col.db_name, col.heading, col.descr].filter(Boolean).join(' ').toLowerCase().includes(q))
        if (!match.length) continue
        hits.push(
          <div key={`h-${fi}-${gname}`} style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--color-text-secondary)', padding: '8px 8px 2px' }}>
            {formTitle(e.sm, fi)} › {groupLabel(e.sm, gname)}
          </div>,
          ...renderColumns(fi, match, 0),
        )
      }
    })
    return hits.length ? hits : (
      <div style={{ padding: 12, fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>No column matches “{filter}”.</div>
    )
  }

  return (
    <div ref={rootRef} style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--color-bg-secondary)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '12px 10px 8px 14px' }}>
        <div style={{ fontWeight: 600, flex: 1 }}>Structure</div>
        {onModelChange && (
          <div style={{ position: 'relative' }}>
            <button type="button" className="btn btn-secondary btn-sm" aria-haspopup="menu" aria-expanded={menu?.kind === 'add'}
              onClick={() => setMenu(menu?.kind === 'add' ? null : { kind: 'add' })}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <Plus size={12} /> Add form <ChevronDown size={12} />
            </button>
            {menu?.kind === 'add' && (
              <div data-tv-menu role="menu" style={{ ...menuStyle, right: 0, top: 'calc(100% + 4px)', width: 260 }}>
                <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--color-text-secondary)', padding: '4px 10px' }}>CHILD OF…</div>
                {entries.map((e, fi) => (
                  <button key={e.formNo} role="menuitem" style={menuItem} onClick={() => addChildForm(e.formNo)}>
                    <span style={{ fontSize: '0.75rem', color: 'var(--color-accent)', fontWeight: 600, minWidth: 24 }}>{e.formNo}</span>
                    {formTitle(e.sm, fi)}
                  </button>
                ))}
                <div style={{ borderTop: '1px solid var(--color-border)', margin: '4px 0' }} />
                <button role="menuitem" style={menuItem} onClick={addUnlinkedForm}
                  title="A bare form with no parent link — for advanced layouts">
                  Unlinked form (advanced)
                </button>
              </div>
            )}
          </div>
        )}
        {onHide && (
          <button type="button" style={iconBtn} onClick={onHide}
            aria-label={drawer ? 'Close structure' : 'Hide structure panel'} title={drawer ? 'Close' : 'Hide structure  ['}>
            {drawer ? <X size={18} /> : <PanelLeftClose size={16} />}
          </button>
        )}
      </div>
      <div style={{ padding: '0 12px 8px' }}>
        <label style={{
          display: 'flex', alignItems: 'center', gap: 8, border: '1px solid var(--color-border)', borderRadius: 6,
          padding: '0 8px', height: drawer ? 40 : 32, background: 'var(--color-input-bg)',
        }}>
          <Search size={13} style={{ color: 'var(--color-text-secondary)', flexShrink: 0 }} />
          <span className="sr-only" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>Filter columns</span>
          <input
            value={filter}
            onChange={e => setFilter(e.target.value)}
            placeholder="Filter columns"
            style={{ border: 'none', outline: 'none', background: 'transparent', font: 'inherit', fontSize: '0.9375rem', flex: 1, minWidth: 0, padding: 0, boxShadow: 'none' }}
          />
          {filter && (
            <button type="button" aria-label="Clear filter" onClick={() => setFilter('')} style={{ ...iconBtn, width: 20, height: 20 }}><X size={12} /></button>
          )}
        </label>
      </div>
      <div style={{ flex: 1, overflow: 'auto', padding: '0 6px 16px' }}>
        {q ? renderFiltered() : renderTree()}
      </div>
      {confirmDialog}
    </div>
  )
}
